import { dataProvider } from "rest-data-provider";

// A server1-4 slot: a physical environment's own url/build info.
export interface OrcaServerSlot {
  url: string;
  hash: string;
  version: string;
}

// A `result` array entry: what ares.web actually renders as a button
// (Sidebar prod entry, or a Beta/Fallback entry in the Helper dropdown).
// Field names intentionally match ares.web's OrcaProdUrlEntry — `server`,
// not `url`, and it carries name/description that a slot alone doesn't have.
export interface OrcaProdUrlEntry {
  server: string;
  name: string;
  description: string;
  hash: string;
  version: string;
}

export const OrcaDeployState = {
  STAGE: 0,
  STABLE: 1,
  UNSTABLE: 2,
} as const;

export type OrcaDeployStateValue =
  (typeof OrcaDeployState)[keyof typeof OrcaDeployState];

export const OrcaDeployStateLabels: Record<OrcaDeployStateValue, string> = {
  [OrcaDeployState.STAGE]: "stage",
  [OrcaDeployState.STABLE]: "stable",
  [OrcaDeployState.UNSTABLE]: "unstable",
};

export type ServerSlotKey = "server1" | "server2" | "server3" | "server4";

export interface OrcaDeployActivityLogRow {
  id: string;
  result: OrcaProdUrlEntry[];
  server1: OrcaServerSlot | null;
  server2: OrcaServerSlot | null;
  server3: OrcaServerSlot | null;
  server4: OrcaServerSlot | null;
  prod: ServerSlotKey;
  second: ServerSlotKey;
  state: OrcaDeployStateValue;
  prodLinkName: string;
  prodLinkDescription: string;
  fallbackLinkName: string;
  fallbackDescription: string;
  betaLinkName: string;
  betaLinkDescription: string;
  activityName: string;
  activityOperatorName: string;
  // Free-form note the operator can attach to an activity (nullable in the
  // schema). It describes the one activity it was written on, so it is never
  // carried forward onto the next appended row.
  comment: string | null;
  createdAt: number;
}

// A slot url must be a bare domain — toEntry prefixes "https://" and
// appends the SSO path unconditionally, so a value that already has a
// protocol, path, query, or whitespace would produce a broken/duplicated
// URL (e.g. "https://https://..." or a URL with an embedded "?" before the
// SSO query is appended).
export const isBareDomain = (value: string): boolean =>
  /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/.test(
    value
  );

const NY_TIME_ZONE = "America/New_York";

// Fixed display format across both list pages, always in Eastern Time
// regardless of the viewing operator's local timezone — e.g.
// "Thu, 13/08/2026, 09:44:57 ET". Intl can't emit this exact shape in one
// call, so pull the parts for America/New_York and assemble them by hand.
export const formatNyTimestamp = (ts: number): string => {
  if (!ts) return "";
  const date = new Date(ts);
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: NY_TIME_ZONE,
    weekday: "short",
  }).format(date);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: NY_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${weekday}, ${get("day")}/${get("month")}/${get("year")}, ${get("hour")}:${get("minute")}:${get("second")} ET`;
};

// Shows the URL (what an operator actually recognizes), not the internal
// slot key (server1/2/3/4), which is just an implementation detail.
export const slotLabel = (
  row: Pick<OrcaDeployActivityLogRow, "server1" | "server2" | "server3" | "server4">,
  key: ServerSlotKey | null
): string => {
  if (!key) return "—";
  const slot = row[key];
  return slot ? slot.url : "(empty)";
};

// Re-fetched right before a create, so a stale `latest` picked up on page
// load (or sitting open for a while) doesn't get built into the next
// activity log row out from under a concurrent operator's more recent one.
export const fetchLatestOrcaDeployActivityLogRow = async (
  apiUrl: string
): Promise<OrcaDeployActivityLogRow | null> => {
  const { data } = await dataProvider(apiUrl).getList({
    resource: "orca-deploy-activity-logs",
    sorters: [{ field: "createdAt", order: "desc" }],
    pagination: { current: 1, pageSize: 1, mode: "server" },
  });
  return (data[0] as OrcaDeployActivityLogRow) ?? null;
};
